import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../api/client';
import { performGlobalLogout } from './authSlice';

export const normalizeCasinoResultsResponse = (response) => {
  const layers = [];
  let value = response;

  for (let depth = 0; depth < 6 && value && typeof value === 'object' && !Array.isArray(value); depth += 1) {
    layers.push(value);

    const rows = ['records', 'results', 'rows', 'items', 'result', 'data']
      .find((key) => Array.isArray(value[key]));
    if (rows) {
      value = value[rows];
      break;
    }

    const nested = ['data', 'result']
      .find((key) => value[key] && typeof value[key] === 'object');
    if (!nested) break;
    value = value[nested];
  }

  const records = Array.isArray(value) ? value : [];
  const totalValue = layers
    .map((layer) =>
      layer.total ??
      layer.totalCount ??
      layer.totalRecords ??
      layer.count ??
      layer.pagination?.total ??
      layer.meta?.total
    )
    .find((candidate) => candidate !== undefined && candidate !== null);
  const parsedTotal = Number(totalValue);

  return {
    records,
    total: Number.isFinite(parsedTotal) && parsedTotal >= 0 ? parsedTotal : records.length,
  };
};

// ============================================================
// ASYNC THUNKS
// ============================================================

// Fetch casino results
export const fetchCasinoResults = createAsyncThunk(
  'casinoResults/fetchCasinoResults',
  async ({ game, date, limit, offset }, { rejectWithValue }) => {
    try {
      const params = {};
      if (date) params.date = date;
      if (limit !== undefined) params.limit = limit;
      if (offset !== undefined) params.offset = offset;

      const response = await api.get(
        `/user-history/casino-result/${encodeURIComponent(game)}`,
        { params }
      );
      // Include the game name in the returned data for extraReducers to use
      return { data: response.data, game: game };
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: 'Failed to fetch casino results' });
    }
  }
);

// This API call is only for initial loading/backup, NOT for real-time round history updates
// Round History should be updated from WebSocket data in aviatorSocketMiddleware
export const aviatoresult = createAsyncThunk(
  'casinoResults/aviatoresult',
  async (_, { rejectWithValue }) => {
    try {
      // Only use this for initial backup/historical data, not for real-time updates
      const response = await api.get(`/aviator/result`);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: 'Failed to fetch casino results',
        }
      );
    }
  }
);

// Update aviator results from WebSocket round completion
export const updateAviatorRoundFromWebSocket = createAsyncThunk(
  'casinoResults/updateAviatorRoundFromWebSocket',
  async (roundData, { rejectWithValue }) => {
    try {
      // Validate the round data
      if (!roundData || !roundData.roundId || roundData.multiplier === undefined) {
        return rejectWithValue('Invalid round data from WebSocket');
      }

      // Return the normalized round data for the reducer to handle
      return {
        roundId: roundData.roundId,
        result: roundData.multiplier,
        settledAt: roundData.crashTime || Date.now(),
        gameName: 'Aviator',
        gameType: 'Aviator'
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: 'Failed to process round completion' });
    }
  }
);

// NOTE: this thunk has no reducer case on purpose - it does not feed Last Result.
// Last Result is loaded by fetchCasinoResults({ game: 'Roulette' }) in ResultsScreen.
export const rouletteresult = createAsyncThunk(
  'casinoResults/rouletteresult',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/roulette/result`);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: 'Failed to fetch casino results',
        }
      );
    }
  }
);

export const roulettRebet = createAsyncThunk(
  'casinoResults/roulettRebet',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/roulette/last-round/${userId}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: 'Failed to fetch casino results',
        }
      );
    }
  }
);

// ============================================================
// REDUCER
// ============================================================

const casinoResultsSlice = createSlice({
  name: 'casinoResults',
  initialState: {
    records: [],
    TwoCardTeenPatti: [],
    dtl: [],
    card32: [],
    bollywood: [],
    baccarat: [],
    Rebet: [],
    lucky7: [],
    roulette: [],
    onedaydt: [],
    aaa: [],
    anderbhaer: [],
    dragantigerSettlementResult: [],
    TwoTweentyTeenPatti: [],
    total: 0,
    loading: false,
    error: null,
    aviator: [], // Round history for Aviator
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCasinoResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCasinoResults.fulfilled, (state, action) => {
        state.loading = false;

        const gameName = action.payload?.game || '';
        const normalized = normalizeCasinoResultsResponse(action.payload?.data);
        const normalizedGame = String(gameName).toLowerCase().replace(/[^a-z0-9]/g, '');
        const gameStateKeys = {
          dragontiger: 'dragantigerSettlementResult',
          dragontigerlion: 'dtl',
          lucky7: 'lucky7',
          bollywoodcasino: 'bollywood',
          andarbahar: 'anderbhaer',
          card32: 'card32',
          teenpatittweenty: 'TwoTweentyTeenPatti',
          teenpattit20: 'TwoTweentyTeenPatti',
          tennpattit20: 'TwoTweentyTeenPatti',
          twocardteenpati: 'TwoCardTeenPatti',
          twocardteenpatti: 'TwoCardTeenPatti',
          baccarat: 'baccarat',
          onedaydragontiger: 'onedaydt',
          roulette: 'roulette',
          aaa: 'aaa',
          amarakbaranthony: 'aaa',
        };

        state.records = normalized.records;
        state.total = normalized.total;

        const gameStateKey = gameStateKeys[normalizedGame];
        if (gameStateKey) {
          state[gameStateKey] = normalized.records;
        }
      })
      .addCase(fetchCasinoResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Error fetching results';
      })

      // Handle original aviatoresult for initial loading - REMOVED in v2.0
      // Round history should be updated from WebSocket data in aviatorSocketMiddleware only
      // api.aviator.result call was causing data corruption by overwriting correct WebSocket updates
      .addCase(aviatoresult.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(aviatoresult.fulfilled, (state, action) => {
        state.loading = false;

        const historicalRounds = normalizeCasinoResultsResponse(action.payload).records
          .flatMap((round) => {
            const result = Number(round?.result ?? round?.multiplier);
            if (round?.roundId == null || !Number.isFinite(result)) return [];

            return [{
              ...round,
              result,
              gameName: round.gameName || 'Aviator',
              gameType: round.gameType || 'Aviator',
            }];
          });
        const seenRoundIds = new Set();
        state.aviator = [...state.aviator, ...historicalRounds].filter((round) => {
          const roundId = String(round.roundId);
          if (seenRoundIds.has(roundId)) return false;
          seenRoundIds.add(roundId);
          return true;
        });
      })
      .addCase(aviatoresult.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Error fetching results';
      })

      // Handle update from WebSocket round completion
      .addCase(updateAviatorRoundFromWebSocket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAviatorRoundFromWebSocket.fulfilled, (state, action) => {
        state.loading = false;

        if (!state.aviator) {
          state.aviator = [];
        }

        const completedRound = action.payload;

        // Add to beginning if not already present (avoid duplicates)
        const exists = state.aviator.some(round => round.roundId === completedRound.roundId);
        if (!exists) {
          state.aviator.unshift(completedRound);
        }

        // Keep only last 100 rounds to prevent memory issues
        if (state.aviator.length > 100) {
          state.aviator = state.aviator.slice(0, 100);
        }
      })
      .addCase(updateAviatorRoundFromWebSocket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Error updating aviator round';
      })

      // Rebet
      .addCase(roulettRebet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(roulettRebet.fulfilled, (state, action) => {
        state.loading = false;
        state.Rebet = action.payload || null;
      })
      .addCase(roulettRebet.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || 'Error fetching results';
      })

      // GLOBAL LOGOUT
      .addCase(performGlobalLogout.fulfilled, (state) => {
        state.records = [];
        state.TwoCardTeenPatti = [];
        state.dtl = [];
        state.card32 = [];
        state.bollywood = [];
        state.baccarat = [];
        state.Rebet = [];
        state.lucky7 = [];
        state.roulette = [];
        state.onedaydt = [];
        state.aaa = [];
        state.anderbhaer = [];
        state.dragantigerSettlementResult = [];
        state.TwoTweentyTeenPatti = [];
        state.total = 0;
        state.loading = false;
        state.error = null;
        state.aviator = []; // Clear aviator round history on logout
      });
  },
});

export default casinoResultsSlice.reducer;